import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import './StatTable.css';

export interface StatColumn {
    key: string;
    title: string;
    type: 'text' | 'number' | 'accuracy' | 'percentage' | 'link';
    width?: string | number;
    align?: 'left' | 'center' | 'right';
    dropdown?: boolean;
    linkHandler?: (row: Record<string, any>) => void;
}

export interface StatTableProps {
    columns: StatColumn[];
    data: Record<string, any>[];
}

const StatTable: React.FC<StatTableProps> = ({ columns, data }) => {
    const getAccuracyColor = (accuracy: number) => {
        // Convert accuracy to opacity for purple background
        const opacity = Math.max(0.2, accuracy / 100);
        return `rgba(99, 102, 241, ${opacity})`;
    };

    const renderCell = (row: Record<string, any>, column: StatColumn) => {
        const value = row[column.key];
        
        switch (column.type) {
            case 'accuracy':
                return (
                    <TableCell
                        key={column.key}
                        className="accuracy-cell"
                        data-accuracy={typeof value === 'number' ? value.toFixed(2) : value}
                        sx={{
                            backgroundColor: getAccuracyColor(value),
                            color: 'white',
                            fontWeight: 600,
                            width: '20%'
                        }}
                    >
                        {typeof value === 'number' ? value.toFixed(2) : value}
                    </TableCell>
                );
            case 'percentage':
                return (
                    <TableCell
                        key={column.key}
                        className="thinkrank-cell"
                        sx={{
                            fontWeight: 500,
                            width: '20%',
                        }}
                    >
                        {typeof value === 'number' ? `${(value * 100).toFixed(2)} %` : value}
                    </TableCell>
                );
            case 'number':
                return (
                    <TableCell
                        key={column.key}
                        className="number-cell"
                        sx={{
                            fontWeight: 500,
                            width: '20%'
                        }}
                    >
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </TableCell>
                );
            case 'link':
                return (
                    <TableCell
                        key={column.key}
                        sx={{
                            fontWeight: 500,
                            width: '25%',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {value === 'View Report' ? (
                            <span 
                                onClick={() => column.linkHandler?.(row)}
                                style={{
                                    color: '#6366f1',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                                onMouseOver={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
                                onMouseOut={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
                            >
                                {value}
                            </span>
                        ) : (
                            <span style={{ color: '#9ca3af', fontSize: '14px' }}>{value}</span>
                        )}
                    </TableCell>
                );
            case 'text':
            default:
                return (
                    <TableCell
                        key={column.key}
                        className="skill-name"
                        sx={{
                            fontWeight: 500,
                            width: '30%',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal'
                        }}
                    >
                        {value}
                    </TableCell>
                );
        }
    };

    return (
        <TableContainer>
            <Table className="skill-table">
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell
                                key={column.key}
                                className={column.dropdown ? 'dropdown' : ''}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 14,
                                    color: '#374151',
                                    borderBottom: '1px solid #e5e7eb',
                                    py: 2,
                                    px: 2.5,
                                    textAlign: column.type === 'text' ? 'left' : 'right',
                                    width: column.type === 'text' ? '40%' : '20%'
                                }}
                            >
                                {column.title}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((row, index) => (
                        <TableRow
                            key={index}
                            sx={{
                                '&:last-child td': {
                                    borderBottom: 'none'
                                }
                            }}
                        >
                            {columns.map((column) => renderCell(row, column))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default StatTable;